import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CONSENT_OVERVIEW_FILTER,
  type ConsentOverviewFilter,
} from '@/api/consents/consentsApi.types';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { SETTINGS_TAB_SEARCH_PARAMETER } from '@/components/assistant/uiActions/assistantDestinations';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useConsentsOverviewQuery } from '@/hooks/useClientConsents';
import { ConsentOverviewFilters } from './ConsentOverviewFilters';
import { ConsentOverviewTable } from './ConsentOverviewTable';

const CONSENT_DOCUMENTS_SETTINGS_PATH =
  APPLICATION_ROUTES.workspaceSettings + '?' + SETTINGS_TAB_SEARCH_PARAMETER + '=consentDocuments';

export function ConsentsPage() {
  const { t } = useTranslation('consents');
  const navigate = useNavigate();
  const { activeWorkspace, isElevatedWorkspaceAccess } = useAuth();
  const isWorkspaceOwner =
    (activeWorkspace !== null && activeWorkspace.isOwner === true) || isElevatedWorkspaceAccess;
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [page, setPage] = useState(1);
  const [overviewFilter, setOverviewFilter] = useState<ConsentOverviewFilter>(
    CONSENT_OVERVIEW_FILTER.all
  );

  useEffect(
    function resetPageOnFilterChange() {
      setPage(1);
    },
    [debouncedSearch, overviewFilter]
  );

  const overviewQuery = useConsentsOverviewQuery(page, debouncedSearch, overviewFilter);
  const overview = overviewQuery.data;
  const hasFilters =
    debouncedSearch.trim().length > 0 || overviewFilter !== CONSENT_OVERVIEW_FILTER.all;

  return (
    <div className="p-6 md:p-8">
      <AdminPageHeader
        title={t('overview.title')}
        subtitle={t('overview.subtitle')}
        action={
          isWorkspaceOwner === true ? (
            <Button
              variant="outline"
              onClick={function goToConsentDocuments() {
                navigate(CONSENT_DOCUMENTS_SETTINGS_PATH);
              }}
            >
              <Icon name="Settings" />
              {t('overview.manageDocuments')}
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex-1">
          <ListSearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('overview.search')}
          />
        </div>
        <ConsentOverviewFilters value={overviewFilter} onChange={setOverviewFilter} />
      </div>

      {overviewQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : overviewQuery.isError === true || overview === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            overviewQuery.refetch();
          }}
        />
      ) : (
        <>
          <ConsentOverviewTable
            columns={overview.columns}
            rows={overview.rows}
            hasFilters={hasFilters}
            onOpenClient={function openClient(clientId) {
              navigate('/workspace/clients/' + clientId);
            }}
          />
          <ListPagination
            page={page}
            pageSize={ADMIN_LIST_PAGE_SIZE}
            totalItems={overview.totalItems}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
